import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const PdfPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfUrl = searchParams.get('url') || '';
  const pdfTitle = searchParams.get('title') || 'Document Preview';

  const [zoom, setZoom] = useState(100);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfTitle + '.pdf';
    link.click();
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <>
      <SEO
        title={`${pdfTitle} - Frames by Gaurav`}
        description={`Preview ${pdfTitle}`}
        canonicalUrl="/pdf-preview"
      />
      <Layout showGoogleReviews={false}>
        <div className="min-h-screen bg-gray-100">
          {/* Header Controls */}
          <div className="sticky top-0 z-50 bg-white shadow-md border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                {/* Title */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Close preview"
                  >
                    <X size={24} />
                  </button>
                  <h1 className="text-xl font-bold text-gray-900 truncate max-w-md">
                    {pdfTitle}
                  </h1>
                </div>

                {/* Controls */}
                <div className="flex items-center space-x-2">
                  {/* Zoom Controls */}
                  <div className="flex items-center space-x-2 border-r pr-4">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoom <= 50}
                      className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Zoom out"
                    >
                      <ZoomOut size={20} />
                    </button>
                    <span className="text-sm font-medium w-12 text-center">
                      {zoom}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoom >= 200}
                      className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Zoom in"
                    >
                      <ZoomIn size={20} />
                    </button>
                  </div>

                  {/* Download Button */}
                  <button
                    onClick={handleDownload}
                    className="flex items-center space-x-2 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                  >
                    <Download size={18} />
                    <span className="text-sm font-medium">Download</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {pdfUrl ? (
                <div className="relative">
                  <iframe
                    src={`${pdfUrl}#view=FitH`}
                    title={pdfTitle}
                    className="w-full transition-all duration-300"
                    style={{
                      height: '80vh',
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: 'top center',
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <p className="text-gray-500 text-lg mb-4">
                    No PDF URL provided
                  </p>
                  <button
                    onClick={handleClose}
                    className="bg-black text-white px-6 py-3 rounded hover:bg-gray-800 transition-colors"
                  >
                    Go Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default PdfPreview;
